<template>
  <div data-component="base-agent-info">
    <div class="avatar">
      <agent-avatar
        :person="agent"
        :avatar="avatar"
        data-avatar
      />
    </div>

    <div
      class="name"
      data-text="agent-name"
    >
      {{ name }}
    </div>

    <div
      class="role"
      data-text="agent-role"
    >
      {{ roleLabel }}
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  PropType,
  toRefs,
  computed,
  getCurrentInstance,
} from 'vue-demi';
import { AgentAvatar, AvatarOptions } from '@onereach/hitl-base-avatars';

import { IAgent } from '@/types';
import { getAgentName } from '@/common';
import { USER_ROLE, USER_ROLE_LABEL } from '@/constants';

export default defineComponent({
  name: 'BaseAgentInfo',
  components: { AgentAvatar },
  props: {
    agent: {
      type: Object as PropType<IAgent>,
      required: true,
    },
    avatar: {
      type: Object as PropType<AvatarOptions>,
      default: undefined,
    },
  },
  setup(props) {
    const { agent } = toRefs(props);
    const vm = getCurrentInstance()!;

    const name = computed(() => {
      return getAgentName(agent.value, vm.proxy.$t.bind(vm.proxy));
    });

    const roleLabel = computed(() => {
      return vm.proxy.$t(USER_ROLE_LABEL[agent.value.role as USER_ROLE]);
    });

    return {
      name,
      roleLabel,
    };
  },
});
</script>

<style lang="scss">
@use '@/styles' as *;

[data-component='base-agent-info'] {
  --base-agent-info-avatar-size: 2.5rem;

  display: grid;
  padding: var(--s-1);
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  column-gap: var(--s-4);
  align-items: center;
  grid-template-areas:
    'avatar name'
    'avatar role';
  overflow: hidden;

  .avatar {
    grid-area: avatar;
    width: var(--avatar-size);
    height: var(--avatar-size);
  }

  .name {
    grid-area: name;
    color: var(--or-c-on-background);
    @include text-overflow();
  }

  .role {
    grid-area: role;
    color: var(--or-c-outline);
    font-size: var(--fs-0);
    @include text-overflow();
  }
}
</style>
